import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from './apiUtils';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from './components/ui/table';
import { Input } from './components/ui/input';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Label } from './components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Alert, AlertDescription } from './components/ui/alert';
import { TrendingUp, TrendingDown, Plus, Trash2, Calendar, AlertCircle, Coffee } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Calendar as CalendarComponent } from './components/ui/calendar';
import { format } from 'date-fns';
import useBusinessDate from './hooks/useBusinessDate';
import { toast } from 'sonner';
import { useNavigation } from './contexts/NavigationContext';

function IncomeExpenses() {
  const { logout } = useNavigation();
  const businessDate = useBusinessDate();
  const [date, setDate] = useState(businessDate);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [incomeData, setIncomeData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState('income');
  const [incomeModified, setIncomeModified] = useState(false);
  const [expensesModified, setExpensesModified] = useState(false);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isAddExpenseCategoryOpen, setIsAddExpenseCategoryOpen] = useState(false);
  const [addCategoryName, setAddCategoryName] = useState('');
  const [addExpenseCategoryName, setAddExpenseCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingExpenseCategory, setAddingExpenseCategory] = useState(false);
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, categoryName: '', todayAmount: 0 });
  const [deleteExpenseCategoryDialog, setDeleteExpenseCategoryDialog] = useState({ open: false, categoryName: '', todayAmount: 0 });
  const [error, setError] = useState('');
  const [accessorySalesTotal, setAccessorySalesTotal] = useState(0);

  // Handler for date selection
  const handleDateSelect = (newDate) => {
    if (!newDate) return;
    setSelectedDate(newDate);
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    setDate(formattedDate);
  };

  // Get the current business date as a Date object for comparison
  const getCurrentBusinessDateObject = () => {
    const [year, month, day] = businessDate.split('-');
    return new Date(year, month - 1, day);
  };

  // Check if the selected date is a past date (before current business date)
  const isPastDate = () => {
    const currentBusinessDate = getCurrentBusinessDateObject();
    const selected = new Date(selectedDate);
    // Set both to start of day for accurate comparison
    currentBusinessDate.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    return selected < currentBusinessDate;
  };

  const defaultIncomeCategories = [
    'Accessories Sale',
    'Sitting',
    'Used bottles/cartons sale',
    'Others'
  ];

  const defaultExpenseCategories = [
    'Meals',
    'Stationery', 
    'Fuel cost',
    'Water bill',
    'Rent',
    'Salaries',
    'Electricity',
    'Damages',
    'Depo charges',
    'Transportation',
    'Police dept',
    'Excise dept',
    'Festival charities',
    'Local Charities',
    'Others'
  ];

  useEffect(() => {
    // Load categories on mount
    const fetchCategories = async () => {
      try {
        const incomeResponse = await apiGet('/api/income-expenses/income-categories');
        const incomeResult = await incomeResponse.json();
        const incomeNames = Array.isArray(incomeResult) ? incomeResult.map(c => c.name) : [];
        
        // Always ensure "Accessories Sale" is at the top
        let finalCategories = incomeNames && incomeNames.length > 0 ? incomeNames : defaultIncomeCategories;
        if (!finalCategories.includes('Accessories Sale')) {
          finalCategories = ['Accessories Sale', ...finalCategories];
        } else if (finalCategories[0] !== 'Accessories Sale') {
          // Move it to the top if it exists but not at position 0
          finalCategories = ['Accessories Sale', ...finalCategories.filter(c => c !== 'Accessories Sale')];
        }
        
        setIncomeCategories(finalCategories);
      } catch (e) {
        setIncomeCategories(defaultIncomeCategories);
      }
      
      try {
        const expenseResponse = await apiGet('/api/income-expenses/expense-categories');
        const expenseResult = await expenseResponse.json();
        const expenseNames = Array.isArray(expenseResult) ? expenseResult.map(c => c.name) : [];
        setExpenseCategories(expenseNames && expenseNames.length > 0 ? expenseNames : defaultExpenseCategories);
      } catch (e) {
        setExpenseCategories(defaultExpenseCategories);
      }
      
      setCategoriesLoaded(true);
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!categoriesLoaded) return;
    setLoading(true);
    fetchIncomeExpenses();
    fetchAccessorySales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, categoriesLoaded]);

  // Fetch accessories sales from daily tracking
  const fetchAccessorySales = async () => {
    try {
      const response = await apiGet(`/api/accessories/daily?date=${date}`);
      if (!response.ok) {
        setAccessorySalesTotal(0);
        return;
      }
      const data = await response.json();
      // API returns { accessories: [...], date, totalItems, ... }
      const accessoriesList = data.accessories || data || [];
      
      // Calculate total sales value from accessories
      // Each item has: opening_stock, received_stock, closing_stock, price_per_unit/mrp
      const totalSales = accessoriesList.reduce((sum, item) => {
        // If sale_value is already calculated, use it
        if (item.sale_value) {
          return sum + (parseFloat(item.sale_value) || 0);
        }
        // Otherwise calculate: sales = total_stock - closing_stock, then multiply by price
        const openingStock = item.opening_stock || 0;
        const receivedStock = item.received_stock || 0;
        const totalStock = openingStock + receivedStock;
        const closingStock = item.closing_stock !== null ? item.closing_stock : totalStock;
        const sales = Math.max(0, totalStock - closingStock);
        const price = parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0;
        return sum + (sales * price);
      }, 0);
      
      console.log(`📦 Accessories sales for ${date}: ₹${totalSales.toFixed(2)}`);
      setAccessorySalesTotal(totalSales);
    } catch (error) {
      console.error('Error fetching accessories sales:', error);
      setAccessorySalesTotal(0);
    }
  };

  // Sync selectedDate with the business date on mount
  useEffect(() => {
    const [year, month, day] = businessDate.split('-');
    const dateObj = new Date(year, month - 1, day);
    setSelectedDate(dateObj);
    setDate(businessDate);
  }, [businessDate]);

  const fetchIncomeExpenses = async () => {
    try {
      // Fetch existing income data
      const incomeResponse = await apiGet(`/api/income-expenses/income?date=${date}`);
      const incomeResult = await incomeResponse.json();

      // Fetch existing expenses data
      const expensesResponse = await apiGet(`/api/income-expenses/expenses?date=${date}`);
      const expensesResult = await expensesResponse.json();

      // Check if there's actually saved data (not just zeros)
      const hasIncomeData = incomeResult.length > 0 && incomeResult.some(item => item.amount > 0);
      const hasExpensesData = expensesResult.length > 0 && expensesResult.some(item => item.amount > 0);

      // Initialize income data with categories (from backend or defaults)
      const incomeCategoryList = (incomeCategories && incomeCategories.length > 0) ? incomeCategories : defaultIncomeCategories;
      const initialIncomeData = incomeCategoryList.map(category => {
        const existingEntry = incomeResult.find(item => item.source === category);
        // For "Accessories Sale", we'll use the fetched accessory sales total (handled in render)
        return {
          category,
          amount: existingEntry ? existingEntry.amount : 0,
          description: existingEntry ? existingEntry.description : '',
          isAutoCalculated: category === 'Accessories Sale'
        };
      });

      // Find orphaned income categories (saved data with categories not in master list)
      const orphanedIncome = incomeResult
        .filter(item => !incomeCategoryList.includes(item.source) && item.amount > 0)
        .map(item => ({
          category: item.source,
          amount: item.amount,
          description: item.description || '',
          isArchived: true
        }));

      // Initialize expenses data with categories  
      const expenseCategoryList = (expenseCategories && expenseCategories.length > 0) ? expenseCategories : defaultExpenseCategories;
      const initialExpensesData = expenseCategoryList.map(category => {
        const existingEntry = expensesResult.find(item => item.category === category);
        return {
          category,
          amount: existingEntry ? existingEntry.amount : 0,
          description: existingEntry ? existingEntry.description : ''
        };
      });

      // Find orphaned expense categories (saved data with categories not in master list)
      const orphanedExpenses = expensesResult
        .filter(item => !expenseCategoryList.includes(item.category) && item.amount > 0)
        .map(item => ({
          category: item.category,
          amount: item.amount,
          description: item.description || '',
          isArchived: true
        }));

      setIncomeData([...initialIncomeData, ...orphanedIncome]);
      setExpensesData([...initialExpensesData, ...orphanedExpenses]);
      
      // Only set as "not modified" if there's actually saved data
      // If no saved data exists, treat as new/unsaved so button shows "Save"
      setIncomeModified(!hasIncomeData);
      setExpensesModified(!hasExpensesData);
    } catch (error) {
      console.error('Error fetching income/expenses data:', error);
      // Initialize with empty data if fetch fails
      const incomeCategoryList = (incomeCategories && incomeCategories.length > 0) ? incomeCategories : defaultIncomeCategories;
      const expenseCategoryList = (expenseCategories && expenseCategories.length > 0) ? expenseCategories : defaultExpenseCategories;
      setIncomeData(incomeCategoryList.map(category => ({ category, amount: 0, description: '' })));
      setExpensesData(expenseCategoryList.map(category => ({ category, amount: 0, description: '' })));
      // New day with no data - should show "Save" button
      setIncomeModified(true);
      setExpensesModified(true);
    }
    setLoading(false);
  };

  const handleConfirmAddCategory = async () => {
    const name = (addCategoryName || '').trim();
    if (!name || addingCategory) return;
    setAddingCategory(true);
    try {
      const response = await apiPost('/api/income-expenses/income-categories', { name });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(err.message || 'Failed to add category');
        // If session expired (401), redirect to login
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
        return;
      }
      const data = await response.json();
      const updatedNames = (data.categories || []).map(c => c.name);
      const ordered = updatedNames && updatedNames.length > 0 ? updatedNames : incomeCategories;

      const preserved = ordered.map(cat => {
        const existing = incomeData.find(i => i.category === cat);
        return existing ? existing : { category: cat, amount: 0, description: '' };
      });

      setIncomeCategories(ordered);
      setIncomeData(preserved);
      setIncomeModified(true);
      setAddCategoryName('');
      setIsAddCategoryOpen(false);
      setError(''); // Clear any errors
      toast.success('Category added successfully');
    } catch (e) {
      setError('Network error while adding category');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleIncomeChange = (index, field, value) => {
    const updatedData = [...incomeData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: field === 'amount' ? (parseFloat(value) || 0) : value
    };
    setIncomeData(updatedData);
    setIncomeModified(true);
  };

  const handleExpenseChange = (index, field, value) => {
    const updatedData = [...expensesData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: field === 'amount' ? (parseFloat(value) || 0) : value
    };
    setExpensesData(updatedData);
    setExpensesModified(true);
  };

  const handleSaveIncome = async () => {
    setSaving(true);
    try {
      // Filter out auto-calculated entries (like Accessories Sale) and empty amounts
      const incomeEntries = incomeData.filter(item => 
        item.amount > 0 && item.category !== 'Accessories Sale'
      );

      const response = await apiPost('/api/income-expenses/save-income', {
        date,
        income: incomeEntries
      });

      if (response.ok) {
        toast.success('Income saved successfully!');
        setError(''); // Clear any errors
        // Refresh data to show saved values
        await fetchIncomeExpenses();
        setIncomeModified(false);
      } else {
        const error = await response.json();
        setError(`Error: ${error.message}`);
        // If session expired (401), redirect to login
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error saving income:', error);
      setError('Network error while saving income');
    }
    setSaving(false);
  };

  const handleSaveExpenses = async () => {
    setSaving(true);
    try {
      const expenseEntries = expensesData.filter(item => item.amount > 0);

      const response = await apiPost('/api/income-expenses/save-expenses', {
        date,
        expenses: expenseEntries
      });

      if (response.ok) {
        toast.success('Expenses saved successfully!');
        setError(''); // Clear any errors
        // Refresh data to show saved values
        await fetchIncomeExpenses();
        setExpensesModified(false);
      } else {
        const error = await response.json();
        setError(`Error: ${error.message}`);
        // If session expired (401), redirect to login
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error saving expenses:', error);
      setError('Network error while saving expenses');
    }
    setSaving(false);
  };

  const getTotalIncome = () => {
    if (!incomeData || !Array.isArray(incomeData) || incomeData.length === 0) return accessorySalesTotal;
    try {
      const total = incomeData.reduce((sum, item) => {
        // For Accessories Sale, use the auto-calculated value
        if (item.category === 'Accessories Sale') {
          return sum + accessorySalesTotal;
        }
        const amount = item && item.amount !== undefined ? parseFloat(item.amount) || 0 : 0;
        return sum + amount;
      }, 0);
      return typeof total === 'number' ? total : 0;
    } catch (error) {
      console.error('Error calculating total income:', error);
      return 0;
    }
  };

  const getTotalExpenses = () => {
    if (!expensesData || !Array.isArray(expensesData) || expensesData.length === 0) return 0;
    try {
      const total = expensesData.reduce((sum, item) => {
        const amount = item && item.amount !== undefined ? parseFloat(item.amount) || 0 : 0;
        return sum + amount;
      }, 0);
      return typeof total === 'number' ? total : 0;
    } catch (error) {
      console.error('Error calculating total expenses:', error);
      return 0;
    }
  };

  const isDefaultIncomeCategory = (name) => {
    if (!name) return false;
    const n = name.toLowerCase();
    return n === 'accessories sale' || n === 'sitting' || n === 'used bottles/cartons sale' || n === 'others';
  };

  const isDefaultExpenseCategory = (name) => {
    if (!name) return false;
    const n = name.toLowerCase();
    const defaults = [
      'meals', 'stationery', 'fuel cost', 'water bill', 'rent',
      'salaries', 'electricity', 'damages', 'depo charges', 'transportation',
      'police dept', 'excise dept', 'festival charities', 'local charities', 'others'
    ];
    return defaults.includes(n);
  };

  // Expense category handlers
  const handleConfirmAddExpenseCategory = async () => {
    const name = (addExpenseCategoryName || '').trim();
    if (!name || addingExpenseCategory) return;
    setAddingExpenseCategory(true);
    try {
      const response = await apiPost('/api/income-expenses/expense-categories', { name });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(err.message || 'Failed to add expense category');
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
        return;
      }
      const data = await response.json();
      const updatedNames = (data.categories || []).map(c => c.name);
      const ordered = updatedNames && updatedNames.length > 0 ? updatedNames : expenseCategories;

      const preserved = ordered.map(cat => {
        const existing = expensesData.find(i => i.category === cat);
        return existing ? existing : { category: cat, amount: 0, description: '' };
      });

      setExpenseCategories(ordered);
      setExpensesData(preserved);
      setExpensesModified(true);
      setAddExpenseCategoryName('');
      setIsAddExpenseCategoryOpen(false);
      setError('');
      toast.success('Expense category added successfully');
    } catch (e) {
      setError('Network error while adding expense category');
    } finally {
      setAddingExpenseCategory(false);
    }
  };

  const handleOpenDeleteExpenseCategoryDialog = (categoryName) => {
    if (!categoryName || isDefaultExpenseCategory(categoryName)) return;
    // Check if this category has amount for today
    const categoryData = expensesData.find(i => i.category === categoryName);
    const todayAmount = categoryData?.amount || 0;
    setDeleteExpenseCategoryDialog({ open: true, categoryName, todayAmount });
  };

  const handleConfirmDeleteExpenseCategory = async () => {
    const categoryName = deleteExpenseCategoryDialog.categoryName;
    setDeleteExpenseCategoryDialog({ open: false, categoryName: '', todayAmount: 0 });
    
    try {
      const response = await apiDelete('/api/income-expenses/expense-categories', { 
        name: categoryName,
        businessDate: businessDate  // Pass business date to delete today's data
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Failed to delete expense category');
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
        return;
      }
      const updatedNames = (data.categories || []).map(c => c.name);
      const ordered = updatedNames && updatedNames.length > 0 ? updatedNames : expenseCategories.filter(c => c !== categoryName);
      const preserved = ordered.map(cat => {
        const existing = expensesData.find(i => i.category === cat);
        return existing ? existing : { category: cat, amount: 0, description: '' };
      });
      setExpenseCategories(ordered);
      setExpensesData(preserved);
      setExpensesModified(true);
      setError('');
      toast.success('Expense category deleted successfully');
    } catch (e) {
      setError('Network error while deleting expense category');
    }
  };

  const handleOpenDeleteDialog = (categoryName) => {
    if (!categoryName || isDefaultIncomeCategory(categoryName)) return;
    // Check if this category has amount for today
    const categoryData = incomeData.find(i => i.category === categoryName);
    const todayAmount = categoryData?.amount || 0;
    setDeleteCategoryDialog({ open: true, categoryName, todayAmount });
  };

  const handleConfirmDelete = async () => {
    const categoryName = deleteCategoryDialog.categoryName;
    setDeleteCategoryDialog({ open: false, categoryName: '', todayAmount: 0 });
    
    try {
      const response = await apiDelete('/api/income-expenses/income-categories', { 
        name: categoryName,
        businessDate: businessDate  // Pass business date to delete today's data
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Failed to delete category');
        // If session expired (401), redirect to login
        if (response.status === 401) {
          setTimeout(() => {
            if (logout) logout();
            else window.location.href = '/login';
          }, 1000);
        }
        return;
      }
      const updatedNames = (data.categories || []).map(c => c.name);
      const ordered = updatedNames && updatedNames.length > 0 ? updatedNames : incomeCategories.filter(c => c !== categoryName);
      const preserved = ordered.map(cat => {
        const existing = incomeData.find(i => i.category === cat);
        return existing ? existing : { category: cat, amount: 0, description: '' };
      });
      setIncomeCategories(ordered);
      setIncomeData(preserved);
      setIncomeModified(true);
      setError(''); // Clear any errors
      toast.success('Category deleted successfully');
    } catch (e) {
      setError('Network error while deleting category');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex justify-center items-center h-screen">
          <div className="text-muted-foreground text-xl">Loading income and expenses...</div>
        </div>
      </div>
    );
  }

  return (
      <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 w-full">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold text-left">Income & Expenses</h1>
            <p className="text-base text-muted-foreground text-left">Record additional income sources and operational expenses</p>
          </div>
          
          {/* Date Picker */}
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg border bg-card">
            <Calendar className="h-5 w-5 text-primary" />
            <div className="text-left">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Business Date</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="h-auto p-0 font-bold text-sm hover:bg-transparent">
                    {format(selectedDate, 'dd-MM-yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date > getCurrentBusinessDateObject()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Past Date Alert */}
        {isPastDate() && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-blue-800">
                <Calendar className="h-4 w-4" />
                <p className="text-sm font-medium">
                  You are viewing past data. Editing is disabled for historical records.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Type Selection Card */}
        <Card>
          <CardContent className="pt-6">
            <RadioGroup 
              value={selectedType} 
              onValueChange={setSelectedType}
              className="flex gap-4 justify-center"
            >
              <Label 
                htmlFor="income" 
                className={`flex items-center gap-3 px-6 py-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedType === 'income' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="income" id="income" />
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">Income</span>
                </div>
              </Label>
              <Label 
                htmlFor="expenses" 
                className={`flex items-center gap-3 px-6 py-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedType === 'expenses' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <RadioGroupItem value="expenses" id="expenses" />
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <span className="font-semibold">Expenses</span>
                </div>
              </Label>
            </RadioGroup>
          </CardContent>
        </Card>

        <div>
          {selectedType === 'income' ? (
            /* Income Table */
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Income
                  </CardTitle>
                  <Button 
                    onClick={() => setIsAddCategoryOpen(true)}
                    disabled={isPastDate()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center w-20">S.No</TableHead>
                        <TableHead className="text-left w-1/4">Category</TableHead>
                        <TableHead className="text-right w-40">Amount (₹)</TableHead>
                        <TableHead className="text-left">Description</TableHead>
                        <TableHead className="text-right w-32"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incomeData.map((item, index) => {
                        const isAccessorySale = item.category === 'Accessories Sale';
                        
                        return (
                          <TableRow key={index} className={isAccessorySale ? 'bg-gradient-to-r from-emerald-50 to-teal-50' : item.isArchived ? 'bg-amber-50/50' : ''}>
                            <TableCell className="text-center w-20 font-medium">{index + 1}</TableCell>
                            <TableCell className="text-left w-1/4">
                              {isAccessorySale ? (
                                <div className="flex items-center gap-2">
                                  <Coffee className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                  <span className="font-semibold text-emerald-800">{item.category}</span>
                                </div>
                              ) : item.isArchived ? (
                                <span className="text-amber-700">{item.category}</span>
                              ) : (
                                item.category
                              )}
                            </TableCell>
                            <TableCell className="text-right w-40">
                              {isAccessorySale ? (
                                <span className="font-bold text-emerald-700">₹{accessorySalesTotal.toFixed(2)}</span>
                              ) : (
                            <Input
                              type="number"
                              className="w-32 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={item.amount || ''}
                              onChange={(e) => handleIncomeChange(index, 'amount', e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              disabled={isPastDate()}
                            />
                              )}
                          </TableCell>
                            <TableCell className="text-left">
                              {isAccessorySale ? (
                                <span className="text-sm text-emerald-600 text-left">Cool drinks, water bottles, sodas etc.</span>
                              ) : (
                            <Input
                              type="text"
                              value={item.description || ''}
                              onChange={(e) => handleIncomeChange(index, 'description', e.target.value)}
                              placeholder="Optional description"
                              disabled={isPastDate()}
                            />
                              )}
                          </TableCell>
                            <TableCell className="text-right w-32">
                              {isAccessorySale ? (
                                <Badge className="bg-emerald-500 text-white">Auto</Badge>
                              ) : item.isArchived ? (
                                <Badge variant="outline" className="border-amber-500 text-amber-600">Archived</Badge>
                              ) : isDefaultIncomeCategory(item.category) ? (
                              <Badge variant="secondary">Default</Badge>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDeleteDialog(item.category)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={isPastDate()}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="font-bold">
                        <TableCell></TableCell>
                        <TableCell className="text-right">Total Income</TableCell>
                        <TableCell className="text-right text-lg">₹{getTotalIncome().toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Expenses Table */
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    Expenses
                  </CardTitle>
                  <Button 
                    onClick={() => setIsAddExpenseCategoryOpen(true)}
                    disabled={isPastDate()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center w-20">S.No</TableHead>
                        <TableHead className="text-left w-1/4">Category</TableHead>
                        <TableHead className="text-right w-40">Amount (₹)</TableHead>
                        <TableHead className="text-left">Description</TableHead>
                        <TableHead className="text-right w-32"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expensesData.map((item, index) => (
                        <TableRow key={index} className={item.isArchived ? 'bg-amber-50/50' : ''}>
                          <TableCell className="text-center font-medium">{index + 1}</TableCell>
                          <TableCell className="text-left">
                            {item.isArchived ? (
                              <span className="text-amber-700">{item.category}</span>
                            ) : (
                              item.category
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              className="w-32 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={item.amount || ''}
                              onChange={(e) => handleExpenseChange(index, 'amount', e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              disabled={isPastDate()}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              value={item.description || ''}
                              onChange={(e) => handleExpenseChange(index, 'description', e.target.value)}
                              placeholder="Optional description"
                              disabled={isPastDate()}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            {item.isArchived ? (
                              <Badge variant="outline" className="border-amber-500 text-amber-600">Archived</Badge>
                            ) : isDefaultExpenseCategory(item.category) ? (
                              <Badge variant="secondary">Default</Badge>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDeleteExpenseCategoryDialog(item.category)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={isPastDate()}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="font-bold">
                        <TableCell></TableCell>
                        <TableCell className="text-right">Total Expenses</TableCell>
                        <TableCell className="text-right text-lg">₹{getTotalExpenses().toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={selectedType === 'income' ? handleSaveIncome : handleSaveExpenses}
            disabled={saving || isPastDate() || (selectedType === 'income' ? !incomeModified : !expensesModified)}
          >
            {saving 
              ? 'Saving...' 
              : isPastDate()
                ? 'Past Date - View Only'
                : (selectedType === 'income' ? !incomeModified : !expensesModified) 
                  ? 'Already Saved' 
                  : `Save ${selectedType === 'income' ? 'Income' : 'Expenses'}`
            }
          </Button>
        </div>

        <Dialog open={isAddCategoryOpen} onOpenChange={(open) => !addingCategory && setIsAddCategoryOpen(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Income Category</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Input
                type="text"
                placeholder="Category name"
                value={addCategoryName}
                onChange={(e) => setAddCategoryName(e.target.value)}
                disabled={addingCategory}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsAddCategoryOpen(false)} 
                disabled={addingCategory}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmAddCategory} 
                disabled={!addCategoryName.trim() || addingCategory}
              >
                {addingCategory ? 'Adding...' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteCategoryDialog.open} onOpenChange={(open) => setDeleteCategoryDialog({ ...deleteCategoryDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Income Category</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              {deleteCategoryDialog.todayAmount > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Warning: You have ₹{deleteCategoryDialog.todayAmount.toLocaleString('en-IN')} recorded under "{deleteCategoryDialog.categoryName}" for today.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Deleting this category will also remove today's entry. Past data will be preserved.
                  </p>
                </div>
              )}
              <p>Are you sure you want to delete the category <strong>"{deleteCategoryDialog.categoryName}"</strong>?</p>
              <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setDeleteCategoryDialog({ open: false, categoryName: '', todayAmount: 0 })}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Expense Category Dialog */}
        <Dialog open={isAddExpenseCategoryOpen} onOpenChange={(open) => !addingExpenseCategory && setIsAddExpenseCategoryOpen(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Expense Category</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Input
                type="text"
                placeholder="Category name"
                value={addExpenseCategoryName}
                onChange={(e) => setAddExpenseCategoryName(e.target.value)}
                disabled={addingExpenseCategory}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsAddExpenseCategoryOpen(false)} 
                disabled={addingExpenseCategory}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmAddExpenseCategory} 
                disabled={!addExpenseCategoryName.trim() || addingExpenseCategory}
              >
                {addingExpenseCategory ? 'Adding...' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Expense Category Dialog */}
        <Dialog open={deleteExpenseCategoryDialog.open} onOpenChange={(open) => setDeleteExpenseCategoryDialog({ ...deleteExpenseCategoryDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Expense Category</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              {deleteExpenseCategoryDialog.todayAmount > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Warning: You have ₹{deleteExpenseCategoryDialog.todayAmount.toLocaleString('en-IN')} recorded under "{deleteExpenseCategoryDialog.categoryName}" for today.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Deleting this category will also remove today's entry. Past data will be preserved.
                  </p>
                </div>
              )}
              <p>Are you sure you want to delete the expense category <strong>"{deleteExpenseCategoryDialog.categoryName}"</strong>?</p>
              <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setDeleteExpenseCategoryDialog({ open: false, categoryName: '', todayAmount: 0 })}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleConfirmDeleteExpenseCategory}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </main>
  );
}

export default IncomeExpenses;
